import React, { useState } from 'react';
import FormField from './FormField';
import QRPreview from './QRPreview';
import ImageCapture from './ImageCapture';
import { saveRMARecord } from '../utils/supabase';

const RMAForm = ({ 
  rma,
  setRma,
  isPallet,
  setIsPallet,
  packagingType,
  setPackagingType,
  packagingCondition,
  setPackagingCondition,
  bulkType,
  setBulkType,
  customBulkNumber,
  setCustomBulkNumber,
  labelSizes,
  setLabelSizes,
  images,
  onImageCapture,
  validationError,
  qrCodeDataUrl,
  generateQrCode
}) => {
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [palletNumber, setPalletNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [odooStatus, setOdooStatus] = useState('');
  
  const getBulkNumber = () => {
    if (bulkType === 'one') return '1';
    if (bulkType === 'two') return '2';
    if (bulkType === 'custom') return customBulkNumber;
    return '';
  };

  const handleBulkTypeChange = (e) => {
    setBulkType(e.target.value);
  };

  const handleLabelSizeChange = (size) => {
    setLabelSizes(prev => ({
      ...prev,
      [size]: !prev[size]
    }));
  };

  const handleGenerateAndSave = async () => {
    if (!rma.trim()) return;
    
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    
    try {
      // First generate QR code
      generateQrCode();
      
      // Convert image data URLs to blobs with better error handling
      const imageBlobs = await Promise.all(images.map(async (imageData, index) => {
        try {
          // Handle both string URLs and object format
          const url = typeof imageData === 'string' ? imageData : imageData.url;
          const name = typeof imageData === 'string' ? `rma-${Date.now()}-${index}.jpg` : (imageData.name || `rma-${Date.now()}-${index}.jpg`);
          const type = typeof imageData === 'string' ? 'image/jpeg' : (imageData.type || 'image/jpeg');
          
          // For data URLs, convert directly without fetch
          if (url.startsWith('data:')) {
            const arr = url.split(',');
            const mime = arr[0].match(/:(.*?);/)[1];
            const bstr = atob(arr[1]);
            let n = bstr.length;
            const u8arr = new Uint8Array(n);
            while(n--) {
              u8arr[n] = bstr.charCodeAt(n);
            }
            return new File([u8arr], name, { type: mime || type });
          } else {
            // For regular URLs, use fetch
            const response = await fetch(url);
            const blob = await response.blob();
            return new File([blob], name, { type: type });
          }
        } catch (error) {
          console.error('Error converting image:', error, imageData);
          throw error;
        }
      }));
      
      // Save record
      await saveRMARecord({
        rmaNumber: rma,
        palletNumber: isPallet === 'Sí' ? palletNumber : null,
        packagingType: packagingType,
        condition: packagingCondition,
        notes: notes,
        metadata: { 
          bulkType: bulkType,
          bulkNumber: getBulkNumber(),
          isPallet: isPallet === 'Sí',
          labelSizes 
        }
      }, imageBlobs);
      
      setSaveSuccess(true);
      
      // Check RMA in Odoo and send message to customer
      let odooResult = { found: false, messageSent: false };
      try {
        setOdooStatus('Consultando RMA en Odoo...');
        
        // First, lookup RMA in Odoo
        const lookupResponse = await fetch('/.netlify/functions/odoo-rma-lookup', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ rmaNumber: rma })
        });
        
        const lookupResult = await lookupResponse.json();
        
        if (lookupResult.found && lookupResult.rma) {
          setOdooStatus('RMA encontrado. Enviando mensaje al cliente...');
          
          // Prepare RMA data for message
          const rmaDataForMessage = {
            isPallet: isPallet,
            palletNumber: isPallet === 'Sí' ? palletNumber : null,
            packagingType: packagingType,
            packagingCondition: packagingCondition,
            bulkNumber: getBulkNumber(),
            notes: notes
          };
          
          // Send message to RMA chatter
          const messageResponse = await fetch('/.netlify/functions/odoo-rma-message', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              rmaId: lookupResult.rma.id,
              rmaNumber: rma,
              rmaData: rmaDataForMessage
            })
          });
          
          const messageResult = await messageResponse.json();
          
          if (messageResult.success) {
            setOdooStatus('✅ Mensaje enviado al cliente exitosamente');
            odooResult = { found: true, messageSent: true, rmaData: lookupResult.rma };
          } else {
            setOdooStatus('⚠️ Error al enviar mensaje al cliente');
            odooResult = { found: true, messageSent: false, error: messageResult.error };
          }
        } else {
          setOdooStatus('ℹ️ RMA no encontrado en Odoo');
          odooResult = { found: false, messageSent: false };
        }
      } catch (odooError) {
        console.error('Error processing RMA in Odoo:', odooError);
        setOdooStatus('⚠️ Error al consultar Odoo');
        odooResult = { found: false, messageSent: false, error: odooError.message };
      }
      
      // Send Slack notification
      try {
        const slackWebhook = import.meta.env.VITE_SLACK_RMA_WEBHOOK || 'https://hooks.slack.com/services/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';
        
        console.log('Sending Slack notification...');
        console.log('Webhook URL:', slackWebhook);
        
        const slackMessage = {
          text: `Nueva entrada RMA registrada: ${rma}`,
          blocks: [
            {
              type: "header",
              text: {
                type: "plain_text",
                text: `🔄 Nueva RMA: ${rma}`
              }
            },
            {
              type: "section",
              fields: [
                {
                  type: "mrkdwn",
                  text: `*Número RMA:* ${rma}`
                },
                {
                  type: "mrkdwn",
                  text: `*En Pallet:* ${isPallet}`
                },
                {
                  type: "mrkdwn",
                  text: `*Número de Pallet:* ${isPallet === 'Sí' ? palletNumber || 'N/A' : 'N/A'}`
                },
                {
                  type: "mrkdwn",
                  text: `*Tipo de Embalaje:* ${packagingType}`
                },
                {
                  type: "mrkdwn",
                  text: `*Estado del Embalaje:* ${packagingCondition}`
                },
                {
                  type: "mrkdwn",
                  text: `*Número de Bultos:* ${getBulkNumber()}`
                }
              ]
            }
          ]
        };
        
        if (notes) {
          slackMessage.blocks.push({
            type: "section",
            text: {
              type: "mrkdwn",
              text: `*Notas adicionales:* ${notes}`
            }
          });
        }
        
        // Add Odoo integration status
        if (odooResult.found) {
          slackMessage.blocks.push({
            type: "section",
            text: {
              type: "mrkdwn",
              text: odooResult.messageSent 
                ? `✅ *Integración Odoo:* RMA encontrado - Mensaje enviado al cliente`
                : `⚠️ *Integración Odoo:* RMA encontrado - Error al enviar mensaje`
            }
          });
        } else {
          slackMessage.blocks.push({
            type: "section",
            text: {
              type: "mrkdwn",
              text: `ℹ️ *Integración Odoo:* RMA no encontrado en el sistema`
            }
          });
        }
        
        slackMessage.blocks.push({
          type: "context",
          elements: [
            {
              type: "mrkdwn",
              text: `Registrado el ${new Date().toLocaleString('es-ES', { timeZone: 'Europe/Madrid' })}`
            }
          ]
        });
        
        // Use proxy function to avoid CORS issues
        const proxyUrl = '/.netlify/functions/slack-proxy';
        const proxyResponse = await fetch(proxyUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(slackMessage)
        });
        
        if (!proxyResponse.ok) {
          const errorText = await proxyResponse.text();
          console.error('Slack notification failed:', errorText);
        } else {
          console.log('Slack notification sent successfully');
        }
      } catch (slackError) {
        console.error('Error sending Slack notification:', slackError);
        // Don't fail the save operation if Slack notification fails
      }
      
      // Clear form after successful save
      setTimeout(() => {
        setRma('');
        setPalletNumber('');
        setIsPallet('');
        setPackagingType('');
        setPackagingCondition('');
        setBulkType('');
        setCustomBulkNumber('');
        setNotes('');
        onImageCapture([]);
        setSaveSuccess(false);
        setOdooStatus('');
      }, 3000);
    } catch (error) {
      console.error('Error saving record:', error);
      setSaveError(error.message || 'Error al guardar el registro');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rma-form active">
      <div className="form-container">
        {validationError && (
          <div className="alert error">
            <span role="img" aria-label="Error">⚠️</span> {validationError}
          </div>
        )}

        <FormField
          label="Número de RMA:"
          id="rma-input"
          type="text"
          value={rma}
          onChange={(e) => setRma(e.target.value)}
          required={true}
          placeholder="Ingresa el número de RMA"
          autoFocus={true}
        />

        <FormField
          label="¿Viene en pallet?"
          id="is-pallet"
          type="radio"
          value={isPallet}
          onChange={(e) => setIsPallet(e.target.value)}
          required={true}
          options={[
            { value: 'Sí', label: 'Sí' },
            { value: 'No', label: 'No' }
          ]}
        />

        {isPallet === 'Sí' && (
          <FormField
            label="Número de Pallet:"
            id="pallet-number"
            type="text"
            value={palletNumber}
            onChange={(e) => setPalletNumber(e.target.value)}
            placeholder="Ingresa el número de pallet"
          />
        )}

        <FormField
          label="Tipo de Embalaje:"
          id="packaging-type"
          type="radio"
          value={packagingType}
          onChange={(e) => setPackagingType(e.target.value)}
          required={true}
          options={[
            { value: 'Original', label: 'Original' },
            { value: 'No Original', label: 'No Original' },
            { value: 'Sin embalaje', label: 'Sin embalaje' }
          ]}
        />

        <FormField
          label="Estado del embalaje:"
          id="packaging-condition"
          type="radio"
          value={packagingCondition}
          onChange={(e) => setPackagingCondition(e.target.value)}
          required={true}
          options={[
            { value: 'Bueno', label: 'Bueno' },
            { value: 'Regular', label: 'Regular' },
            { value: 'Malo', label: 'Malo' }
          ]}
        />

        <FormField
          label="Número de Bultos:"
          id="bulk-type"
          type="radio"
          value={bulkType}
          onChange={handleBulkTypeChange}
          required={true}
          options={[
            { value: 'one', label: '1 Bulto' },
            { value: 'two', label: '2 Bultos' },
            { value: 'custom', label: 'Más de 2:' }
          ]}
        />

        {bulkType === 'custom' && (
          <FormField
            label="Especificar número:"
            id="custom-bulk-number"
            type="number"
            value={customBulkNumber}
            onChange={(e) => setCustomBulkNumber(e.target.value)}
            required={true}
            placeholder="Número de bultos"
          />
        )}


        <FormField
          label="Notas adicionales:"
          id="notes"
          type="textarea"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Observaciones o comentarios adicionales"
        />

        <div className="form-group">
          <label>Tamaño de Etiqueta:</label>
          <div className="checkbox-group">
            <div className="checkbox-option">
              <input
                type="checkbox"
                id="label-size-large"
                checked={labelSizes.large || false}
                onChange={() => handleLabelSizeChange('large')}
              />
              <label htmlFor="label-size-large">110mm (alto) x 50mm (ancho)</label>
            </div>
            <div className="checkbox-option">
              <input
                type="checkbox"
                id="label-size-small"
                checked={labelSizes.small || false}
                onChange={() => handleLabelSizeChange('small')}
              />
              <label htmlFor="label-size-small">57mm (alto) x 32mm (ancho)</label>
            </div>
          </div>
        </div>

        <div className="form-group">
          <label>Imagen:</label>
          <div className="input-container">
            <ImageCapture
              onImageCapture={onImageCapture}
              existingImages={images}
            />
          </div>
        </div>

        <div className="button-group">
          <button 
            className="button save-button" 
            onClick={handleGenerateAndSave}
            disabled={!rma.trim() || saving}
          >
            {saving ? 'Procesando...' : 'Generar QR y Guardar Registro RMA'}
          </button>
        </div>

        {saveSuccess && (
          <div className="success-message">
            ✅ Registro RMA guardado exitosamente
          </div>
        )}
        
        {odooStatus && (
          <div className="info-message">
            {odooStatus}
          </div>
        )}
        
        {saveError && (
          <div className="error-message">
            ❌ Error: {saveError}
          </div>
        )}
      </div>

      {qrCodeDataUrl && (
        <QRPreview 
          qrCodeDataUrl={qrCodeDataUrl} 
          qrData={`RMA: ${rma}\nPallet: ${isPallet}\nEmbalaje: ${packagingType}\nEstado: ${packagingCondition}\nBultos: ${getBulkNumber()}`}
          labelSize={labelSizes.large ? 'large' : labelSizes.small ? 'small' : ''}
        />
      )}
    </div>
  );
};

export default RMAForm;